import { useCreateVetterUserMutation } from '@codezo/graphql';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';

export function InviteMemberModal({
	open,
	onOpenChange,
	vetterEntityId,
	onSuccess,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	vetterEntityId: string;
	onSuccess?: () => void;
}) {
	const [email, setEmail] = useState('');
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [phone, setPhone] = useState('');
	const [notes, setNotes] = useState('');
	const [sendInvitation, setSendInvitation] = useState(true);
	const [emailError, setEmailError] = useState<string | null>(null);

	const [createVetterUser, { loading }] = useCreateVetterUserMutation();

	const resetForm = () => {
		setEmail('');
		setFirstName('');
		setLastName('');
		setPhone('');
		setNotes('');
		setSendInvitation(true);
		setEmailError(null);
	};

	const validate = () => {
		if (!email.trim()) {
			setEmailError('Email is required');
			return false;
		}
		if (!/^\S+@\S+$/.test(email.trim())) {
			setEmailError('Invalid email');
			return false;
		}
		setEmailError(null);
		return true;
	};

	const handleSubmit = async () => {
		if (!validate() || !firstName.trim() || !lastName.trim()) return;

		try {
			const { data } = await createVetterUser({
				variables: {
					input: {
						vetterEntityId,
						email: email.trim(),
						firstName: firstName.trim(),
						lastName: lastName.trim(),
						phone: phone.trim() || undefined,
						notes: notes.trim() || undefined,
						sendInvitation,
					},
				},
			});

			if (data?.createVetterUser?.success) {
				toast.success(
					sendInvitation
						? 'Team member invited successfully'
						: 'Team member added — no invitation sent'
				);
				resetForm();
				onOpenChange(false);
				onSuccess?.();
			} else {
				toast.error(data?.createVetterUser?.message || 'Failed to invite team member');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to invite team member');
		}
	};

	const canSubmit = email.trim() && firstName.trim() && lastName.trim();

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				if (!next) resetForm();
				onOpenChange(next);
			}}
		>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Invite team member</DialogTitle>
					<DialogDescription>
						Send an invitation to join this vetter entity.
					</DialogDescription>
				</DialogHeader>

				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-4">
							<div className="flex flex-col gap-1.5">
								<Label htmlFor="member-email">
									Email <span className="text-destructive">*</span>
								</Label>
								<Input
									id="member-email"
									type="email"
									value={email}
									onChange={(e) => {
										setEmail(e.target.value);
										if (emailError) setEmailError(null);
									}}
									placeholder="user@example.com"
								/>
								{emailError && (
									<p className="text-xs text-destructive">{emailError}</p>
								)}
							</div>

							<div className="grid grid-cols-2 gap-3">
								<div className="flex flex-col gap-1.5">
									<Label htmlFor="member-first-name">
										First name <span className="text-destructive">*</span>
									</Label>
									<Input
										id="member-first-name"
										value={firstName}
										onChange={(e) => setFirstName(e.target.value)}
										placeholder="First name"
									/>
								</div>
								<div className="flex flex-col gap-1.5">
									<Label htmlFor="member-last-name">
										Last name <span className="text-destructive">*</span>
									</Label>
									<Input
										id="member-last-name"
										value={lastName}
										onChange={(e) => setLastName(e.target.value)}
										placeholder="Last name"
									/>
								</div>
							</div>

							<div className="flex flex-col gap-1.5">
								<Label htmlFor="member-phone">Phone</Label>
								<Input
									id="member-phone"
									type="tel"
									value={phone}
									onChange={(e) => setPhone(e.target.value)}
									placeholder="+45 12 34 56 78"
								/>
							</div>

							<div className="flex flex-col gap-1.5">
								<Label htmlFor="member-notes">Notes</Label>
								<Textarea
									id="member-notes"
									value={notes}
									onChange={(e) => setNotes(e.target.value)}
									placeholder="Role, availability, anything worth remembering"
									rows={3}
								/>
							</div>

							<label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-line-1 bg-surface p-2.5">
								<Checkbox
									checked={sendInvitation}
									onCheckedChange={(next) => setSendInvitation(next === true)}
									className="mt-0.5"
								/>
								<span className="flex flex-col gap-0.5">
									<span className="text-sm text-text-1">
										Send invitation email
									</span>
									<span className="text-xs text-text-3">
										Off records the member without emailing them. You can invite
										them later from their row.
									</span>
								</span>
							</label>
						</div>
					</DialogBody>

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => {
								resetForm();
								onOpenChange(false);
							}}
							disabled={loading}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={!canSubmit || loading}>
							{loading
								? sendInvitation
									? 'Inviting…'
									: 'Adding…'
								: sendInvitation
									? 'Send invite'
									: 'Add member'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
